// ════════════════════════════════════════════════════════════════════════════
// BOT DECK — Marine Tekkadan Aggro
// Every hook here is called by js/bot_logic/bot-engine.js — see that file's
// header comment for the full contract and why this can't reuse the
// registered player-side card effects in js/effects/*.js directly. "Friendly"
// in this file always means state.oppField/oppHand/oppBase (the bot's own
// side); "enemy" always means state.playerField/hand/base (the human).
// ════════════════════════════════════════════════════════════════════════════

// ── Shared local helpers ──────────────────────────────────────────────────

// Zock (ST11-003) gains <Blocker> while another friendly (Marine) Unit is in
// play (its own passive, js/effects/blue.js — checked live here via
// hasNativeKeyword rather than re-deriving the condition, so this always
// agrees with whatever actually grants it). This governs ATTACKING only (see
// chooseAttackOrder below) — Zock is deliberately held back from attacking
// SPECIFICALLY so it's available to block, so this must never also gate the
// blocking decision (shouldBlock below always blocks once Zock is eligible
// at all — that's the entire point of the holdback). Per the deck notes:
// don't attack with Zock when the bot's own board is low on defense (3 or
// less shields and no base), OR whenever Zock currently has Blocker at all
// (the notes' literal example — "ST01-001 gundam is in play" — is just one
// way Zock's own passive condition gets satisfied; what actually matters is
// the Blocker grant itself, so that's what's checked here rather than
// hardcoding one specific card as the trigger).
//
// Overrides (always attack anyway): the enemy currently has no ACTIVE
// Blocker at all — either because every Blocker they have is rested, or
// they have none — so there's nothing to redirect the attack onto; or the
// bot's own unit count on the field outnumbers the enemy's active Blocker
// count by enough that most attacks get through regardless (e.g. the bot
// has 3 Units against the enemy's 1 Blocker). Both cases make the holdback
// pointless this turn — there's no reason to keep Zock in reserve against a
// threat that can't actually materialize right now.
function mtaShouldHoldZockBack(zockCard) {
  const lowDefense = state.oppShield <= 3 && !state.oppBase;
  const isBlockerNow = hasNativeKeyword(zockCard, 'Blocker');
  if (!lowDefense && !isBlockerNow) return false;

  const enemyActiveBlockers = state.playerField.filter(c => c && !c.rested && hasNativeKeyword(c, 'Blocker'));
  if (enemyActiveBlockers.length === 0) return false;
  const botUnitCount = state.oppField.filter(c => c).length;
  if (botUnitCount - enemyActiveBlockers.length >= 2) return false;

  return true;
}

// Mirrors the targets chooseNextDeployOrActivate's Char Aznable tiers
// (1b/1c/1d below) would actually find this turn — Z'Gok specifically, the
// Gundam fallback, or the generic highest-AP fallback — without needing to
// run the full decision tree. Used by deferFirstAttackPass: Char's own
// [When Paired] effect ("All friendly (Marine) Units get AP+1 during this
// turn") is a BLANKET buff that applies to every Marine unit, not just the
// one Char pairs to — if it pairs in the deploy loop after other Marine
// units already attacked in a pre-deploy pass, that buff comes too late to
// matter for them. CONFIRMED BUG found via live play (and reproduced in
// testing): Char's Z'Gok attacked unpaired, THEN Char paired and logged the
// AP+1 buff — one phase too late to affect that attack.
function mtaCharWillPairThisTurn(ctx) {
  const char = ctx.hand.find(c => c.cardNo === 'ST11-011');
  if (!char || (ctx.res + ctx.exRes) < (parseInt(char.cost) || 0)) return false;
  if (ctx.field.some(c => c && c.cardNo === 'ST11-001' && !c.pilot)) return true; // Tier 1c: Z'Gok
  const amuroInHand = ctx.hand.some(c => c.cardNo === 'ST01-010');
  if (!amuroInHand && ctx.field.some(c => c && c.cardNo === 'ST01-001' && !c.pilot && !cardCantAttackNow(c))) {
    return true; // Tier 1b: Gundam fallback (only when Amuro isn't going to claim it instead)
  }
  if (ctx.enemyBase && ctx.field.some(c => c && !c.pilot)) return true; // Tier 1d: generic fallback
  return false;
}

// Ptolemaios (ST07-015): "While a rested friendly (CB) Unit is in play,
// this Base can't receive damage from enemy Units that are Lv.3 or lower,
// other than Unit tokens." Rather than track the live "is there currently a
// rested (CB) Unit on the enemy's side" condition turn to turn, the bot
// treats the immunity as always potentially live — hold Lv.3-or-lower
// non-token attackers back from it entirely instead of risking a wasted
// attack that does nothing. Lv.4+ Units and Unit tokens are always let
// through (tokens are explicitly excluded from the immunity by the card's
// own text).
function mtaShouldHoldBackFromPtolemaios(card, ctx) {
  if (ctx.enemyBase?.cardNo !== 'ST07-015') return false;
  if (isUnitToken(card)) return false;
  return (parseInt(card.level) || 0) <= 3;
}

registerBotDeck('marine-tekkadan-aggro', {
  name: 'Marine Aggro',
  // Opponent-profile-panel-only fields (see renderBotProfilePanel(), game.js)
  // — distinct from `name` above, which is the DECK's own name and is what
  // every in-game log line ("Marine Aggro — played X.") already uses via
  // botName() (bot-engine.js); these three are purely cosmetic flavor for
  // the in-match bot profile panel and never read anywhere else.
  personaName: 'Bot Char Aznable',
  playstyle: 'Aggressive',
  decklistSource: '1st Place Milwaukee Regionals 2026',
  representativeCard: 'ST11-001',
  faceIcon: 'assets/bot_face/marine.webp',
  decklist: {
    'ST11-009': 4, 'ST11-008': 4, 'GD05-050': 4, 'GD01-008': 4, 'ST11-007': 4,
    'ST11-003': 4, 'GD03-056': 4, 'ST11-001': 4, 'ST01-001': 4, 'GD03-001': 1,
    'ST01-010': 4, 'ST11-011': 4, 'GD01-100': 2, 'ST01-015': 1, 'ST02-016': 2,
  },
  sideboard: {},

  // "Before playing anything, attack the shield area first when possible" —
  // see bot-engine.js's runBotMainPhase.
  attacksBeforeDeploys: true,

  // Exception to the above: defer the pre-deploy attack pass whenever
  // Gundam (ST01-001) is about to end up paired THIS turn but hasn't been
  // yet — either (a) Gundam AND Amuro Ray are BOTH still in hand (deploy
  // Gundam, then pair Amuro, before anyone attacks), or (b) Gundam is
  // ALREADY on the field, unpaired, and otherwise attack-eligible, with a
  // pilot in hand that can pair to it (Amuro, or Char Aznable as the
  // fallback — see chooseNextDeployOrActivate's Tier 1b above). Confirmed
  // live: without case (b), an already-on-field unpaired Gundam gets swept
  // into the PRE-deploy attack pass and swings unpaired, since it's
  // attack-eligible from the very start of the turn — the deploy loop
  // (where the pairing actually happens) never gets a chance to run first.
  // Without case (a), a unit already on the field wastes its attack
  // chipping the enemy base before Gundam is even deployed. Either way the
  // whole point — see chooseAttackOrder's 3+ HP base rule below — is
  // freeing every other unit from the base fight so they can go straight
  // for the shield area, which only works if Gundam gets paired before the
  // turn's one real attack pass.
  deferFirstAttackPass(ctx) {
    const gundamInHand = ctx.hand.some(c => c.cardNo === 'ST01-001');
    const amuroInHand = ctx.hand.some(c => c.cardNo === 'ST01-010');
    if (gundamInHand && amuroInHand) return true;

    const charInHand = ctx.hand.some(c => c.cardNo === 'ST11-011');
    const gundamOnField = ctx.field.find(c => c && c.cardNo === 'ST01-001' && !c.pilot && !cardCantAttackNow(c));
    if (gundamOnField && (amuroInHand || charInHand)) return true;

    // General case: Char Aznable is about to pair to SOMETHING this turn
    // (most commonly Z'Gok) — defer so its blanket AP+1-to-all-(Marine)
    // aura is live before anyone attacks, not just whichever unit it
    // happens to pair to. See mtaCharWillPairThisTurn's own comment above.
    if (mtaCharWillPairThisTurn(ctx)) return true;

    return false;
  },

  shouldMulligan(hand) {
    const cost1Units = hand.filter(c => c.type === 'unit' && (parseInt(c.cost) || 0) === 1).length;
    return cost1Units < 2;
  },

  // ── Main Phase driver ──────────────────────────────────────────────────
  // Called repeatedly by bot-engine.js until it returns null. Each tier
  // re-evaluates the CURRENT hand/field every call, so an action that was
  // gated out this iteration (e.g. Guntank with no legal target yet) is
  // simply reconsidered — or left in hand for a later turn — on the next one.
  chooseNextDeployOrActivate(ctx) {
    const hand = ctx.hand;
    const fieldUnits = ctx.field.filter(Boolean);

    // Tier 0 — "If it's the beginning of the player's turn with no units on
    // the field, always activate [White Base] first, then deploy units in
    // hand." Only applies once ST01-015 is already the base.
    if (ctx.base?.cardNo === 'ST01-015' && fieldUnits.length === 0 && mtaCanActivateWhiteBase(ctx)) {
      return { kind: 'activateBase' };
    }

    // Tier 1a — Amuro Ray (ST01-010) pairing priority: Gundam (ST01-001)
    // unconditionally; Gundam NT-1 (GD03-001) only once the opponent has a
    // rested Unit to hit with its own [When Paired] effect (otherwise hold
    // Amuro — see the deck notes' NT-1 caveat). Checked before Char's own
    // pairing tiers below — Gundam/NT-1 are reserved for Amuro first; Char
    // only ever takes Gundam as a fallback (Tier 1b below) once Amuro isn't
    // available to claim it, so Amuro's [When Paired] effect always gets a
    // chance to actually trigger.
    const amuro = hand.find(c => c.cardNo === 'ST01-010');
    const amuroAffordable = amuro && (ctx.res + ctx.exRes) >= (parseInt(amuro.cost) || 0);
    if (amuroAffordable) {
      const gundam = ctx.field.find(c => c && c.cardNo === 'ST01-001' && !c.pilot);
      if (gundam) return { kind: 'pairPilot', pilotUid: amuro.uid, unitUid: gundam.uid };
      const nt1 = ctx.field.find(c => c && c.cardNo === 'GD03-001' && !c.pilot);
      if (nt1 && ctx.enemyField.some(c => c && c.rested)) {
        return { kind: 'pairPilot', pilotUid: amuro.uid, unitUid: nt1.uid };
      }
    }

    const char = hand.find(c => c.cardNo === 'ST11-011');
    const charAffordable = char && (ctx.res + ctx.exRes) >= (parseInt(char.cost) || 0);

    // Tier 1b — Char Aznable (ST11-011), Gundam-priority exception: an
    // unpaired, attack-eligible Gundam (ST01-001) already on the field beats
    // Char's own Z'Gok (ST11-001, Tier 1c below) as a pairing target,
    // whenever Amuro isn't in hand to claim Gundam instead (the block above
    // already pairs Amuro->Gundam unconditionally whenever both exist, so
    // reaching here means Amuro isn't available). Gundam's own [During
    // Pair] AP+1-to-all-friendly-Units aura only needs ANY pilot paired
    // (not specifically Amuro) to turn on, and the point is to have that
    // aura live before the attack phase — see chooseAttackOrder's 3+ HP
    // base rule. Gated on "can attack" so a rested Gundam doesn't steal
    // Char away from an available Z'Gok when Amuro might still show up
    // next turn instead.
    if (!amuro && charAffordable) {
      const gundamNoPilot = ctx.field.find(c => c && c.cardNo === 'ST01-001' && !c.pilot && !cardCantAttackNow(c));
      if (gundamNoPilot) return { kind: 'pairPilot', pilotUid: char.uid, unitUid: gundamNoPilot.uid };
    }

    // Tier 1c — Char Aznable, Z'Gok-specific half: an active Char's Z'Gok is
    // Char's own reserved target whenever Tier 1b's Gundam-priority
    // exception above doesn't apply.
    if (charAffordable) {
      const zgok = ctx.field.find(c => c && c.cardNo === 'ST11-001' && !c.pilot);
      if (zgok) return { kind: 'pairPilot', pilotUid: char.uid, unitUid: zgok.uid };
    }

    // Tier 1d — Char Aznable's generic fallback: "if the opponent has a
    // base, pair this card to the unit with the highest AP." Gundam/NT-1
    // are excluded here (reserved for Amuro/Tier 1b above) — Char only
    // takes them if Amuro either isn't in hand or already passed on them
    // this iteration.
    if (charAffordable && ctx.enemyBase) {
      const unpaired = fieldUnits.filter(c => !c.pilot && c.cardNo !== 'ST01-001' && c.cardNo !== 'GD03-001');
      if (unpaired.length > 0) {
        const best = unpaired.reduce((a, b) => (effectiveBp(b) > effectiveBp(a) ? b : a), unpaired[0]);
        return { kind: 'pairPilot', pilotUid: char.uid, unitUid: best.uid };
      }
    }

    // Tier 2 — Guntank's own conditional deploy gate (its [Deploy] target
    // choice itself lives in onDeploy['GD01-008'] below).
    const guntank = hand.find(c => c.cardNo === 'GD01-008');
    if (guntank && ctx.canPlayCard(guntank)) {
      const enemyHasRested = ctx.enemyField.some(c => c && c.rested);
      const enemyEmpty = ctx.enemyField.every(c => !c);
      if (enemyHasRested || enemyEmpty) return { kind: 'deploy', uid: guntank.uid };
    }

    // Tier 3 — cost-1 units first, as a blanket priority (Guntank excluded —
    // it owns its own gate above and must NOT be deployed through this
    // generic tier when tier 2's condition isn't met).
    const cost1 = hand.find(c => c.cardNo !== 'GD01-008' && c.type === 'unit' &&
      (parseInt(c.cost) || 0) === 1 && ctx.canPlayCard(c));
    if (cost1) return { kind: 'deploy', uid: cost1.uid };

    // Tier 4 — every other unit in hand, cheapest first.
    const otherUnits = hand.filter(c => c.type === 'unit' && ctx.canPlayCard(c))
      .sort((a, b) => (parseInt(a.cost) || 0) - (parseInt(b.cost) || 0));
    if (otherUnits.length > 0) return { kind: 'deploy', uid: otherUnits[0].uid };

    // Tier 5 — White Base (ST01-015) deploy-from-hand gate: never alongside
    // an existing base, and only when no Units are in play, OR at Lv.3+ with
    // nothing else playable, OR at 3-or-fewer shields.
    const whiteBase = hand.find(c => c.cardNo === 'ST01-015');
    if (whiteBase && ctx.canPlayCard(whiteBase) && !ctx.base) {
      const noUnits = fieldUnits.length === 0;
      const nothingElsePlayable = !hand.some(c => c.uid !== whiteBase.uid && ctx.canPlayCard(c));
      const lv3PlusNoPlays = ctx.level >= 3 && nothingElsePlayable;
      const lowShields = ctx.shield <= 3;
      if (noUnits || lv3PlusNoPlays || lowShields) return { kind: 'deployBase', uid: whiteBase.uid };
    }

    // Tier 6 — Corsica Base (ST02-016) deploy-from-hand gate: only when no
    // other eligible Unit is in hand, and either Lv.3 with no EX Resource
    // available or Lv.4+.
    const corsica = hand.find(c => c.cardNo === 'ST02-016');
    if (corsica && ctx.canPlayCard(corsica) && !ctx.base) {
      const noOtherUnits = !hand.some(c => c.type === 'unit' && c.uid !== corsica.uid && ctx.canPlayCard(c));
      const lv3NoEx = ctx.level === 3 && ctx.exRes === 0;
      if (noOtherUnits && (lv3NoEx || ctx.level >= 4)) return { kind: 'deployBase', uid: corsica.uid };
    }

    // Tier 7 — White Base's [Activate·Main], once it's already the base:
    // fire once no more cost-1 units are left to play, during the bot's
    // first 4 turns (state.turn counts both players' turns, so the bot's
    // own 4th turn is state.turn === 8 when it goes second — approximated
    // here as "<= 8" rather than tracked per-side exactly).
    if (ctx.base?.cardNo === 'ST01-015' && mtaCanActivateWhiteBase(ctx)) {
      const anyCost1Left = hand.some(c => (parseInt(c.cost) || 0) === 1 && ctx.canPlayCard(c));
      if (!anyCost1Left && ctx.turn <= 8) return { kind: 'activateBase' };
    }

    // Tier 8 — "A Show of Resolve" (GD01-100): filler, only once nothing
    // else in hand is playable.
    const resolve = hand.find(c => c.cardNo === 'GD01-100');
    if (resolve && ctx.canPlayCard(resolve)) {
      const anythingElsePlayable = hand.some(c => c.uid !== resolve.uid && ctx.canPlayCard(c));
      if (!anythingElsePlayable) return { kind: 'activateCommand', uid: resolve.uid };
    }

    return null;
  },

  // ── [Deploy] effects ───────────────────────────────────────────────────
  onDeploy: {
    // Guntank: "Choose 1 rested enemy Unit. Deal 1 damage to it." — lowest
    // HP among rested candidates, per the deck notes.
    'GD01-008': (card) => {
      const candidates = state.playerField.filter(c => c && c.rested);
      if (candidates.length === 0) return;
      const target = candidates.reduce((lowest, c) => (effectiveHp(c) < effectiveHp(lowest) ? c : lowest), candidates[0]);
      dealDamageToUnit(target, 1, { fromEnemy: true });
      addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
      render(); setupDropZones();
    },

    // Gundam Barbatos Adapt: "Choose 1 of your Units and 1 enemy Unit. Deal
    // 1 damage to them." Deck notes: damage another friendly Unit with 2+ HP
    // left first when possible, then the lowest-HP enemy (AP tiebreak,
    // highest first). Never choose an ally sitting at 1 HP — that 1 damage
    // would destroy it — fall back to damaging Barbatos Adapt itself
    // instead (always legal: the card text has no "other" restriction).
    'GD03-056': (card) => {
      const friendlyOthersWithHp2Plus = state.oppField.filter(c => c && c.uid !== card.uid && effectiveHp(c) >= 2);
      const enemies = state.playerField.filter(c => c);
      if (enemies.length === 0) { addLog(`${card.name} — not enough targets. No effect.`); return; }
      const fTarget = friendlyOthersWithHp2Plus.length > 0 ? friendlyOthersWithHp2Plus[0] : card;
      let eTarget = enemies[0];
      enemies.forEach(c => {
        if (effectiveHp(c) < effectiveHp(eTarget) ||
            (effectiveHp(c) === effectiveHp(eTarget) && effectiveBp(c) > effectiveBp(eTarget))) {
          eTarget = c;
        }
      });
      dealDamageToUnit(fTarget, 1);
      dealDamageToUnit(eTarget, 1, { fromEnemy: true });
      addLog(`${card.name} — dealt 1 damage to ${fTarget.name} and 1 damage to ${eTarget.name}.`);
      render(); setupDropZones();
    },

    // Char's Z'Gok: "If another friendly (Marine) Unit is in play, choose 1
    // enemy Unit Lv.2 or lower. Return it to the bottom of its owner's
    // deck." Deck notes: a Lv.2-or-lower Blocker first, else the Lv.2 unit
    // with the highest AP and/or HP.
    'ST11-001': (card) => {
      const hasOtherMarine = state.oppField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Marine)'));
      if (!hasOtherMarine) return;
      const candidates = state.playerField.filter(c => c && (parseInt(c.level) || 0) <= 2);
      if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`); return; }
      const blockers = candidates.filter(c => hasNativeKeyword(c, 'Blocker'));
      const pool = blockers.length ? blockers : candidates;
      let target = pool[0];
      pool.forEach(c => {
        if ((effectiveBp(c) + effectiveHp(c)) > (effectiveBp(target) + effectiveHp(target))) target = c;
      });
      BotEngine.botReturnPlayerUnitToDeckBottom(target);
      addLog(`${card.name} — returned ${target.name} to the bottom of its owner's deck.`);
      render(); setupDropZones();
    },
  },

  // ── [Destroyed] effects — see game.js's triggerCardEffect for why this
  // can't just be the real registered handler (js/effects/purple.js's own
  // GD05-050 handler explicitly skips unless ctx.ownerSide === 'player',
  // since its effect touches deck/trash data that's only ever real on the
  // owner's own client) ───────────────────────────────────────────────────
  onDestroyed: {
    // Kapool: "[Destroyed] If it is your opponent's turn and a friendly
    // (Marine) Unit is in play, draw 1." "Your opponent" is the human from
    // the bot's own perspective, so this fires when state.myTurn is true
    // (the human's own turn — the common case: they attacked through and
    // killed this). By the time [Destroyed] fires the card is already off
    // state.oppField (the real handler, js/effects/purple.js, has the same
    // comment), so Kapool's own trait is checked directly alongside the
    // rest of the field, same as the real handler does with playerField.
    'ST11-009': (card) => {
      if (!state.myTurn) return; // must be the human's (the bot's "opponent") turn
      const hasMarine = (card.trait || '').includes('(Marine)') ||
        state.oppField.some(c => c && (c.trait || '').includes('(Marine)'));
      if (!hasMarine) return;
      const drawn = BotEngine.botDrawCards(1);
      if (drawn.length > 0) {
        state.oppHand.push(drawn[0]);
        state.oppHandCount = state.oppHand.length;
        addLog(`${card.name} — [Destroyed] opponent's turn, friendly (Marine) Unit in play: drew a card.`);
      } else {
        addLog(`${card.name} — [Destroyed] opponent's turn, friendly (Marine) Unit in play: deck empty, no draw.`);
      }
      render();
    },

    // Gundam Exia Repair: "[Destroyed] Place the top 2 cards of your deck
    // into your trash." No choice to make — always mills exactly 2 (or
    // however many remain) off the bot's own real oppTempDeck.
    'GD05-050': (card) => {
      if (!state.oppTempDeck) return;
      const top = state.oppTempDeck.filter(isInDeck).slice(0, 2);
      if (top.length === 0) { addLog(`${card.name} — deck is empty, nothing to mill.`); return; }
      top.forEach(entry => {
        const dbEntry = cardDb[entry.cardNo];
        entry.state = 'trash';
        state.oppTrashCount++;
        if (dbEntry) {
          const milled = buildGameCardFromDb(dbEntry, entry.copyVer);
          state.oppTrash.push(toTrashCard(milled));
          addLog(`${card.name} — [Destroyed] milled ${milled.name} to trash.`);
        }
      });
      state.oppDeckCount = state.oppTempDeck.filter(isInDeck).length;
      render(); setupDropZones();
    },
  },

  // ── Base [Deploy] effects (beyond the generic "add 1 Shield to hand",
  // already handled by bot-engine.js's applyOppBaseDeployEffect) ─────────
  onDeployBase: {
    // Corsica Base: "Add 1 of your Shields to your hand. Then, IF IT IS YOUR
    // TURN, deploy 1 Tallgeese Unit token. If it is your turn and a card
    // named 'Corsica Base' is in your trash, deploy 2 Leo tokens instead."
    // The token half is conditional on it being the BOT's own turn — this
    // hook also fires when Corsica Base bursts from the bot's shield zone
    // (BotEngine.deployBaseFromShield), which just as often happens during
    // the HUMAN's turn (whenever they attack through to a shield), where
    // state.myTurn is true and no token should deploy at all. Confirmed bug:
    // this check was missing entirely, so a shield-burst Corsica Base always
    // deployed a token regardless of whose turn it actually was.
    'ST02-016': (card) => {
      if (state.myTurn) return; // not the bot's turn — shield-to-hand already happened, nothing else to do
      const hasCorsicaInTrash = state.oppTrash.some(c => (c.name || '').includes('Corsica Base'));
      if (hasCorsicaInTrash) {
        BotEngine.deployTokenToOppField('T-004');
        BotEngine.deployTokenToOppField('T-004');
        addLog(`${card.name} — deployed 2 Leo tokens.`);
      } else {
        const token = BotEngine.deployTokenToOppField('T-005');
        if (token) addLog(`${card.name} — deployed Tallgeese token.`);
      }
      render(); setupDropZones();
    },
  },

  // ── Base [Activate·Main] effects ───────────────────────────────────────
  onActivateBase: {
    // White Base: deploys a Gundam/Guncannon/Guntank token scaled to the
    // bot's current unit count — no choice involved.
    'ST01-015': (base) => {
      const unitCount = state.oppField.filter(c => c != null).length;
      const tokenId = unitCount === 0 ? 'T-001' : unitCount === 1 ? 'T-002' : 'T-003';
      const tokenName = unitCount === 0 ? 'Gundam' : unitCount === 1 ? 'Guncannon' : 'Guntank';
      const token = BotEngine.deployTokenToOppField(tokenId);
      if (token) addLog(`${base.name} — deployed ${tokenName} token.`);
    },
  },

  // ── Command [Main] effects ─────────────────────────────────────────────
  onActivateCommand: {
    'GD01-100': (card) => {
      const drawn = BotEngine.botDrawCards(2);
      state.oppHand.push(...drawn);
      state.oppHandCount = state.oppHand.length;
      addLog(`${card.name} — drew ${drawn.length} card(s).`);
      render(); setupDropZones();
    },
  },

  // ── [When Paired] effects (fires on whichever cardNo owns the text —
  // the pilot's, the unit's, or both) ────────────────────────────────────
  onPair: {
    // Amuro Ray: "Choose 1 active enemy Unit with 5 or less HP. Rest it."
    // Deck notes: a Blocker with 4+ HP first, else any unit with 5-or-less HP.
    'ST01-010': (pilot) => {
      const candidates = state.playerField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
      if (candidates.length === 0) { addLog(`${pilot.name} — no active enemy Units with 5 or less HP.`); return; }
      const blockers4Plus = candidates.filter(c => hasNativeKeyword(c, 'Blocker') && effectiveHp(c) >= 4);
      const target = (blockers4Plus.length ? blockers4Plus : candidates)[0];
      setRested(target, true);
      addLog(`${pilot.name} — ${target.name} rested.`);
      render(); setupDropZones();
    },

    // Char Aznable: no onPair entry needed — "All friendly (Marine) Units get
    // AP+1" is a pure continuous passive (registerCardEffect('ST11-011', ...)
    // in js/effects/blue.js, globalPilotApBonus), live-evaluated via
    // effectiveBp() for as long as Char stays paired on EITHER side. Nothing
    // needs to happen at the moment of pairing itself.

    // Gundam NT-1's own [When Paired]: "Choose 1 rested enemy Unit. Deal 1
    // damage to it. When this effect destroys an enemy Unit, draw 1." Deck
    // notes: always the lowest-HP rested enemy.
    'GD03-001': (unit) => {
      const candidates = state.playerField.filter(c => c && c.rested);
      if (candidates.length === 0) { addLog(`${unit.name} — no rested enemy Units.`); return; }
      const target = candidates.reduce((lowest, c) => (effectiveHp(c) < effectiveHp(lowest) ? c : lowest), candidates[0]);
      const targetUid = target.uid;
      dealDamageToUnit(target, 1, { fromEnemy: true });
      if (!state.playerField.find(c => c && c.uid === targetUid)) {
        const drawn = BotEngine.botDrawCards(1);
        if (drawn.length > 0) {
          state.oppHand.push(drawn[0]);
          state.oppHandCount = state.oppHand.length;
          addLog(`${unit.name} — destroyed enemy Unit, drew a card.`);
        }
      }
      render(); setupDropZones();
    },
  },

  // ── Attack phase ────────────────────────────────────────────────────────
  // Returns the EXACT ordered list of attacker uids for this turn — any
  // eligible attacker left out (Zock, under its holdback condition) simply
  // doesn't attack.
  chooseAttackOrder(ctx) {
    // Ptolemaios holdback applies uniformly to every attacker, before any
    // of the ordering tiers below get a chance to push a Lv.3-or-lower unit
    // to the front — see mtaShouldHoldBackFromPtolemaios's own comment.
    const attackers = ctx.attackers.filter(c => !mtaShouldHoldBackFromPtolemaios(c, ctx));
    const ordered = [];

    // Gundam (ST01-001): attack FIRST when the enemy base has 4+ HP, unless
    // the enemy has an active Blocker with 5+ AP.
    const gundam = attackers.find(c => c.cardNo === 'ST01-001');
    const enemyBaseHp = ctx.enemyBase ? effectiveHp(ctx.enemyBase) : 0;
    const enemyHasBigBlocker = ctx.enemyField.some(c => c && !c.rested && hasNativeKeyword(c, 'Blocker') && effectiveBp(c) >= 5);
    if (gundam && ctx.enemyBase && enemyBaseHp >= 4 && !enemyHasBigBlocker) ordered.push(gundam.uid);

    // Gundam, once paired (its own [During Pair] grants every friendly Unit
    // AP+1 during the bot's turn — already folded into effectiveBp() live,
    // no manual +1 needed here): if there are other units on the field and
    // the enemy base's HP is still higher than what those other, lower-AP
    // units could do to it even with that boost, send Gundam in first —
    // its own (normally higher, pair-boosted) AP is the bot's best shot at
    // making a dent, so it shouldn't go last after the attack phase has
    // already been spent on units that can't break the base at all.
    if (gundam && gundam.pilot && !ordered.includes(gundam.uid) && ctx.enemyBase) {
      const others = attackers.filter(c => c.uid !== gundam.uid);
      const othersCantBreakBaseAlone = others.length > 0 &&
        others.every(c => effectiveBp(c) < effectiveHp(ctx.enemyBase));
      if (othersCantBreakBaseAlone) ordered.push(gundam.uid);
    }

    // Gundam, once paired with ANY pilot (most commonly Amuro Ray, deployed
    // the same turn specifically to set this combo up — see
    // chooseNextDeployOrActivate's Tier 1b above): whenever the enemy has a
    // base with 3+ HP, send Gundam in FIRST, before any other unit. Goal:
    // let Gundam alone (pair-boosted AP) solo the base, so the rest of the
    // board's lower-AP units aren't wasted attacking a base they can't
    // break and can go after the shield area instead.
    if (gundam && gundam.pilot && !ordered.includes(gundam.uid) && ctx.enemyBase && enemyBaseHp >= 3) {
      ordered.push(gundam.uid);
    }

    // Gundam Leopard first, then Gundam Exia Repair ("first or second").
    ['ST11-007', 'GD05-050'].forEach(cardNo => {
      const unit = attackers.find(c => c.cardNo === cardNo);
      if (unit && !ordered.includes(unit.uid)) ordered.push(unit.uid);
    });

    const rest = attackers.filter(c =>
      !ordered.includes(c.uid) && !(c.cardNo === 'ST11-003' && mtaShouldHoldZockBack(c)));
    return [...ordered, ...rest.map(c => c.uid)];
  },

  // Gundam (ST01-001): the enemy base once it has 5+ HP unpaired, or 3+ HP
  // once paired (pairing is what lets Gundam alone threaten to solo the
  // base — see chooseAttackOrder's matching 3+ HP ordering rule above; this
  // target rule has to track that same threshold or Gundam would get sent
  // in first and then just hit the shield area anyway). Everyone else:
  // shield area first, then base, then direct.
  chooseAttackTarget(attacker, validTargets, ctx) {
    const shieldTarget = validTargets.find(t => t.isShield);
    const baseTarget   = validTargets.find(t => t.type === 'base');
    const directTarget = validTargets.find(t => t.isDirect);

    if (attacker.cardNo === 'ST01-001' && baseTarget && ctx.enemyBase) {
      const hpThreshold = attacker.pilot ? 3 : 5;
      if (effectiveHp(ctx.enemyBase) >= hpThreshold) return baseTarget;
    }
    return shieldTarget || baseTarget || directTarget || validTargets[0];
  },

  // ── Defense (human attacks the bot) ────────────────────────────────────
  // Always blocks once eligible (the engine's own eligible-blocker filter
  // already requires <Blocker> to even reach this hook) — mtaShouldHoldZockBack
  // governs whether Zock ATTACKS, never whether it blocks; holding it back
  // from attacking exists specifically so it's free to block.
  shouldBlock() {
    return true;
  },

  chooseBlockTarget(willing) {
    let best = willing[0];
    willing.forEach(c => { if (effectiveHp(c) < effectiveHp(best)) best = c; });
    return best.uid;
  },
});

// White Base's [Activate·Main] cost is RES② with no "Rest this Base"
// component (see getBaseActivationCost) — mirrors ST01-015's own registered
// canActivate (js/effects/blue.js: `() => findNextFieldSlot() !== -1`)
// against the bot's own field.
function mtaCanActivateWhiteBase(ctx) {
  const base = ctx.base;
  if (!base || base.baseAbilityUsedThisTurn) return false;
  if ((ctx.res + ctx.exRes) < 2) return false;
  return ctx.findEmptySlot() !== -1;
}
